import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrendingUp, UserMinus, Search, UserCheck } from 'lucide-react';
import { useFriends } from '@/hooks/useFriends';
import { useUserFollowingForList } from '@/hooks/useUserFollowingForList';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface FollowingListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId?: string; // If provided, show following for this specific user
  onFollowingChanged?: () => void; // Callback when following list changes
}

export function FollowingListDialog({ open, onOpenChange, userId, onFollowingChanged }: FollowingListDialogProps) {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const isOwnProfile = !userId || userId === authUser?.id;
  
  // Use appropriate hook based on whether it's own profile or another user's
  const { following: ownFollowing, loading: ownLoading, unfollowUser } = useFriends();
  const { following: otherFollowing, loading: otherLoading } = useUserFollowingForList(userId);
  
  const following = isOwnProfile ? ownFollowing : otherFollowing;
  const loading = isOwnProfile ? ownLoading : otherLoading;
  
  const { followUser, getRelationshipStatus } = useFriends();
  const [searchQuery, setSearchQuery] = useState('');

  // Adapt the data structure for compatibility
  const adaptedFollowing = useMemo(() => {
    if (!following) return [];
    
    if (isOwnProfile && Array.isArray(following)) {
      // For own profile, following comes from useFriends with different structure
      return following.map(f => ({
        id: f.id,
        user_id: f.friend_profile?.user_id || '',
        full_name: f.friend_profile?.full_name || null,
        email: f.friend_profile?.email || '',
        avatar_url: f.friend_profile?.avatar_url || null
      }));
    } else {
      // For other users, following comes from useUserFollowingForList
      return following.map(f => ({
        id: f.user_id,
        user_id: f.user_id,
        full_name: f.full_name,
        email: f.email,
        avatar_url: f.avatar_url
      }));
    }
  }, [following, isOwnProfile]);

  const filteredFriends = useMemo(() => {
    return adaptedFollowing.filter(friend => 
      friend.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      friend.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [adaptedFollowing, searchQuery]);

  const handleUnfollow = async (friendUserId: string) => {
    if (!isOwnProfile) return; // Only allow unfollowing on own profile
    
    try {
      await unfollowUser(friendUserId);
      // Notify parent component to refresh the following count
      onFollowingChanged?.();
    } catch (error) {
      console.error('Error unfollowing user:', error);
    }
  };

  const handleFollow = async (targetUserId: string) => {
    try {
      await followUser(targetUserId);
      // Notify parent component to refresh the following count
      onFollowingChanged?.();
    } catch (error) {
      console.error('Error following user:', error);
    }
  };

  const handleViewProfile = (targetUserId: string) => {
    navigate(`/profile/${targetUserId}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Amigos que sigues
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar por nombre o email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8">Cargando amigos...</div>
          ) : filteredFriends && filteredFriends.length > 0 ? (
            filteredFriends.map((friend) => (
              <Card key={friend.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                   <div className="flex items-center justify-between">
                     <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => handleViewProfile(friend.user_id)}>
                       <Avatar className="h-10 w-10">
                         <AvatarImage src={friend.avatar_url || "/placeholder.svg"} alt={friend.full_name || "Usuario"} />
                         <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-primary-foreground">
                           {friend.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                         </AvatarFallback>
                       </Avatar>
                       <div>
                         <h3 className="font-medium text-gray-900">{friend.full_name || 'Usuario'}</h3>
                         <p className="text-sm text-gray-600">{friend.email}</p>
                       </div>
                     </div>
                     
                     {isOwnProfile ? (
                       <Button
                         size="sm"
                         variant="outline"
                         onClick={() => handleUnfollow(friend.user_id)}
                         className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                       >
                         <UserMinus className="h-3 w-3 mr-1" />
                         Dejar de seguir
                       </Button>
                     ) : (
                       <div className="flex gap-2">
                         {getRelationshipStatus(friend.user_id) === 'none' && (
                           <Button
                             size="sm"
                             onClick={() => handleFollow(friend.user_id)}
                             className="text-xs"
                           >
                             Seguir
                           </Button>
                         )}
                         {getRelationshipStatus(friend.user_id) === 'they_follow_me' && (
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => handleFollow(friend.user_id)}
                             className="text-xs"
                           >
                             Seguir de vuelta
                           </Button>
                         )}
                         {getRelationshipStatus(friend.user_id) === 'mutual_following' && (
                           <div className="text-xs text-green-600">
                             Amigos
                           </div>
                         )}
                         {getRelationshipStatus(friend.user_id) === 'i_follow_them' && (
                           <div className="text-xs text-primary">
                             Siguiendo
                           </div>
                         )}
                       </div>
                     )}
                   </div>
                </CardContent>
              </Card>
            ))
           ) : following && following.length > 0 ? (
             <div className="text-center py-8 text-gray-500">
               No se encontraron resultados para "{searchQuery}"
             </div>
           ) : (
             <div className="text-center py-8 text-gray-500">
               {isOwnProfile ? "No sigues a ningún jugador aún" : "Este usuario no sigue a ningún jugador aún"}
             </div>
           )}
        </div>
      </DialogContent>
    </Dialog>
  );
}